import { useTranslation } from 'react-i18next';
import { Icons } from '../common/ui-component';
import UploadFiles from '../files/UploadFiles';
import { useNavigate, useLocation } from 'react-router-dom';
import { FileObject } from 'react-mui-dropzone';
import { OrderFileType } from '../files/order-file-type.type';
import TakePicture from './TakePicture';
import { reviewHairLossPhotosHash } from './constants';
import { Page } from './ui';
import { useEffect, useState } from 'react';
import * as amplitude from '@amplitude/analytics-browser';
import { RemoteFile } from '../files';
import { CustomProperties } from '../events/analytics-event.dto';

type Props = {
  handleUploadPhotos: (file: FileObject[], type: string) => void;
  handleDeletePhoto: (file: FileObject, index: number, type: string) => void;
  otherPhotos: FileObject[];
  handleTakePictureClick: (image: string, type: OrderFileType, onClose: () => void) => Promise<void>;
  disableTakePictureButton: boolean;
  processedWithoutPhotosMode: boolean;
  setCameraDetectedAndAllowed: (value: boolean) => void;
  existingPhotos: RemoteFile[];
  onContinueWithoutUploadingClick: () => void;
  driverLicenseRequired: boolean;
  videoRequired: boolean;
  sendAnalyticsEvent: (eventName: string, eventProperties: CustomProperties, userProperties: CustomProperties) => void;
};

const HairLossUpload = ({
  handleUploadPhotos,
  handleDeletePhoto,
  otherPhotos,
  handleTakePictureClick,
  disableTakePictureButton,
  setCameraDetectedAndAllowed,
  processedWithoutPhotosMode,
  existingPhotos,
  onContinueWithoutUploadingClick,
  driverLicenseRequired,
  videoRequired,
  sendAnalyticsEvent,
}: Props) => {
  const { t } = useTranslation('photosPage');
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const [isTakePictureMode, setIsTakePictureMode] = useState(false);

  const addMorePhotosMode = otherPhotos.length > 0 || existingPhotos.length > 0;

  useEffect(() => {
    if (addMorePhotosMode) {
      amplitude.track('photo_flow_add_more');
      sendAnalyticsEvent('Clicked CTA Add More Photos', { CTA_name: 'Add More Photos' }, {});
    } else {
      amplitude.track('photo_flow_start');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addMorePhotosMode]);

  const onPhotoUpload = (file: FileObject[]) => {
    handleUploadPhotos(file, 'other');
    sendAnalyticsEvent(
      'Selected Photo',
      { upload_method: 'fileupload', CTA_name: 'Photo Submitted' },
      { photo_status: 'complete', photo_volume: file.length },
    );

    navigate(`${pathname}${search}${reviewHairLossPhotosHash}`);
  };

  const onTakePictureSendClick = async (image: string, onClose: () => void) => {
    await handleTakePictureClick(image, 'other', onClose);
    sendAnalyticsEvent(
      'Selected Photo',
      { upload_method: 'capture', CTA_name: 'Photo Submitted' },
      { photo_status: 'complete', photo_volume: 1 },
    );
    navigate(`${pathname}${search}${reviewHairLossPhotosHash}`);
  };

  const onTakePictureClick = () => {
    sendAnalyticsEvent('Clicked CTA Take photo', { CTA_name: 'Take photo' }, {});
    setIsTakePictureMode(true);
  };

  const handleOnUploadClick = () => {
    sendAnalyticsEvent('Clicked CTA Upload photo', { CTA_name: 'Upload photo' }, {});
  };

  return (
    <>
      <Page>
        <Page.Title>
          {addMorePhotosMode
            ? t('uploadMorePhotos')
            : processedWithoutPhotosMode || !driverLicenseRequired || videoRequired
            ? t('uploadYourPhotos')
            : t('uploadYourPhotosToGetYourOrder')}
        </Page.Title>
        <Page.Text>{addMorePhotosMode ? '' : t('allItTakesIsTwoMinutes')}</Page.Text>
        <Page.UploadBox>
          <Page.UploadBox.Image>
            <Icons.HairLoss />
          </Page.UploadBox.Image>
          <Page.UploadBox.Container>
            <Page.UploadBox.Container.Title>{t('hairLossPhotos')}</Page.UploadBox.Container.Title>
            <Page.UploadBox.Container.Text>
              {t('select')} <b>{t('takePhoto')}</b> {t('toUseCameraOr')}&nbsp;
              <b>{t('upload')} </b>
              {t('toSelectExistingPhotos')}
            </Page.UploadBox.Container.Text>
            <Page.UploadBox.Container.Action>
              <Page.Button
                className="take-photo"
                disabled={disableTakePictureButton}
                onClick={() => onTakePictureClick()}
              >
                <Page.Button.Custom
                  className={`${disableTakePictureButton ? 'tw-bg-[#AAAAAA]' : 'tw-bg-secondary tw-border-secondary'}`}
                >
                  {t('takePhoto')}
                </Page.Button.Custom>
              </Page.Button>
              <UploadFiles
                onSuccess={(file) => onPhotoUpload(file)}
                onDelete={(file, index) => handleDeletePhoto(file, index, 'other')}
                files={otherPhotos}
                onUploadClick={() => handleOnUploadClick()}
              />
            </Page.UploadBox.Container.Action>
            {disableTakePictureButton && !otherPhotos.length && (
              <Page.ErrorText>{t('noWebcamDetected')}</Page.ErrorText>
            )}
            {otherPhotos.length > 0 && (
              <div className="[&_span]:tw-py-0 tw-block tw-pt-4 lg:tw-pt-4">
                <Page.Button onClick={() => onContinueWithoutUploadingClick()} className="tw-bg-transparent">
                  <Page.Button.Custom
                    className="tw-font-bold tw-text-base tw-hover:tw-font-bold tw-text-black tw-hover:tw-text-black 
                  tw-underline tw-hover:underline"
                  >
                    {t('continueWithoutUploading')}
                  </Page.Button.Custom>
                </Page.Button>
              </div>
            )}
          </Page.UploadBox.Container>
        </Page.UploadBox>
      </Page>
      {isTakePictureMode && (
        <TakePicture
          onClose={() => setIsTakePictureMode(false)}
          onTakePicture={(image: string) => onTakePictureSendClick(image, () => setIsTakePictureMode(false))}
          setCameraDetectedAndAllowed={setCameraDetectedAndAllowed}
          useFrontCamera
        />
      )}
    </>
  );
};

export default HairLossUpload;
